import { TowerArchetype } from "../data/balance";
import { Fix64 } from "../maths/fix64";

/**
 * What a damageless tower's level adds to its own effect (Kevin 2026-10-24; towers.json `levelGrowth`): the shooters'
 * level multiplies their damage (tables.levelMult), and these three grow what they do instead — a Frost hit's slow and
 * its length (the slow capped at `slowFractionMax`), the Beacon's aura damage bonus, the Hex Totem's curse before its
 * tier multiplier. Level 1 is the archetype's own numbers. Pure; read by combat, the auras, the curse and the previews.
 */

const steps = (level: number): number => Math.max(0, level - 1);

/** A Frost hit's (fraction, ticks) at tower level `level`, grown from the tier's own pair. */
export function slowAtLevel(a: TowerArchetype, level: number, base: readonly [Fix64, number]): readonly [Fix64, number] {
  const g = a.levelGrowth, n = steps(level);
  if (n === 0 || (g.slowFraction.isZero() && g.slowTicks === 0)) return base;
  const fraction = base[0].add(g.slowFraction.mulInt(n));
  return [Fix64.min(fraction, Fix64.max(g.slowFractionMax, base[0])), base[1] + g.slowTicks * n];
}

/** The damage bonus a Beacon's aura lends at tower level `level`, grown from the tier's own bonus. */
export function auraBonusAtLevel(a: TowerArchetype, level: number, base: Fix64): Fix64 {
  return base.add(a.levelGrowth.auraDamageBonus.mulInt(steps(level)));
}

/** The Hex Totem's extra damage taken at tower level `level`, before the tier multiplier. */
export function hexTakenAtLevel(a: TowerArchetype, level: number): Fix64 {
  return a.hexDamageTaken.add(a.levelGrowth.hexDamageTaken.mulInt(steps(level)));
}
